/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { injectIntl } from 'react-intl';
import { message } from "antd";
import { SearchOutlined, CloseCircleFilled, LeftOutlined, RightOutlined } from '@ant-design/icons';
import connect from "react-redux/es/connect/connect";
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import IntlMessages from "../../../../util/IntlMessages";
import {
    assignToMe,
    changeConversationSender,
    conversationsPageLoaded,
    createConversation,
    markConversationRead,
    setAssigned,
    setChatId,
    setTabId, turnScrollOn
} from "../../../../appRedux/actions";
import { getConversationByNumber, sortMessagesMyQueuedTeam } from "../utils";
import { addFrontPlusIfMissing } from "../../../../util/helpers";
import ConversationsLink from "../ConversationsLink/ConversationsLink";
import InboxRow from "../Inbox/InboxRow";
import { matchesSearch, newestFirst, useNow } from "../Inbox/inbox";
import { isEditableTarget, takeConversation } from "../Inbox/actions";
import './ConversationsList.less';
import '../Inbox/Inbox.less';

// Conversations per page: as many rows as fit the list's height (no scrolling to reach the pager), at least
// MIN_PAGE and at most MAX_PAGE (the backend's page limit); recomputed when the window is resized.
const MIN_PAGE = 5;
const MAX_PAGE = 200;
const ROW_FALLBACK = 72;
const TAB_NAMES = {'1': 'mine', '2': 'queued', '3': 'team', '4': 'spam'};

const ConversationsList = ({
                               global,
                               accounts,
                               messages,
                               classList,
                               intl,
                               setTabId,
                               setChatId,
                               markConversationRead,
                               createConversation,
                               changeConversationSender,
                               turnScrollOn,
                               assignToMe,
                               setAssigned,
                               conversationsPageLoaded
                           }) => {
    const t = (id, defaultMessage, values) => intl.formatMessage({id, defaultMessage}, values);
    const [query, setQuery] = useState('');
    const searchRef = useRef(null);
    const rowRefs = useRef({});
    const listRef = useRef(null);
    const now = useNow(30000);

    // Open (or start) the conversation with a number: from ?contact=<id> (resolved here, so the number is never
    // in the URL) or the older ?phoneNumber=.
    const openNumber = rawNumber => {
        const number = parsePhoneNumberFromString(addFrontPlusIfMissing(rawNumber));
        if (!number?.isValid()) return;
        const countryCodeAndNumber = number.countryCallingCode + number.nationalNumber;
        const existingConversation = getConversationByNumber(messages, countryCodeAndNumber);
        const senderNumber = accounts.phoneNumbersAll.find(entry => entry.isDefault)?.number;

        if (existingConversation) {
            setChatId(existingConversation._id);
            changeConversationSender(senderNumber, countryCodeAndNumber);
            turnScrollOn();
        } else {
            createConversation(countryCodeAndNumber, senderNumber);
            setChatId(messages[messages.length - 1]?._id);
            turnScrollOn();
        }
    };

    useEffect(() => {
        if (!accounts.phoneNumbersAll) return;
        if (global.contactId && /^[0-9a-f]{24}$/i.test(global.contactId)) {
            axios.get(`${process.env.REACT_APP_API_URL}/contacts/id/${global.contactId}`)
                .then(({data}) => data?.phone && openNumber(data.phone))
                .catch(error => console.log(error));
        } else if (global.phoneNumber) {
            openNumber(global.phoneNumber);
        }
    }, [accounts.phoneNumbersAll]);

    const onSelectConversation = (conversationElement) => {
        if (!conversationElement.isRead && conversationElement.conversationId) {
            markConversationRead(conversationElement.conversationId);
        }

        setChatId(conversationElement._id);
    };

    const onTake = conversation => {
        takeConversation(conversation, {assignToMe, setAssigned, setChatId, setTabId});
        if (!conversation.isRead && conversation.conversationId) markConversationRead(conversation.conversationId);
        message.success(t('chats.conversationAssigned', 'Conversation assigned'));
    };

    const sorted = sortMessagesMyQueuedTeam(messages);
    const tabs = [
        {key: '1', label: t('chats.tab.mine', 'Mine'), list: sorted.my},
        {key: '2', label: t('chat.queued', 'Queued'), list: sorted.queued},
        {key: '3', label: t('chat.teamChats', 'Team'), list: sorted.team},
        {key: '4', label: t('chat.spam', 'Spam'), list: sorted.spam}
    ];
    const activeKey = String(global.tabId || '2');
    const queued = activeKey === '2';
    const active = tabs.find(tab => tab.key === activeKey) || tabs[1];

    // Every tab newest activity first (the queue still shows how long each has waited), a screenful per page.
    // The list is asked from the backend page by page (GET /conversations?paged=true): pages, counts and the search
    // cover every conversation, not only those loaded. A backend without paging answers with the plain array: then
    // the list is paged here from the loaded conversations, as before.
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [server, setServer] = useState(null);
    const [pagedUnavailable, setPagedUnavailable] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setSearchQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);
    // Cursor of each page (the last conversation of the page before it): the next page starts after what was shown,
    // so a conversation jumping to the top meanwhile is neither repeated nor skipped. Page 1 needs none.
    const cursors = useRef({});
    useEffect(() => {
        cursors.current = {};
        setPage(1);
    }, [activeKey, searchQuery, pageSize]);


    // Changes in the loaded conversations (new messages, assignments, spam) refresh the page shown.
    const signature = useMemo(() => messages.map(c => `${c._id}|${c.assignedTo || ''}|${c.isSpam ? 1 : 0}|${c.isRead ? 1 : 0}|${c.lastMessage?.time || ''}`).join(','), [messages]);
    const [settledSignature, setSettledSignature] = useState(signature);
    useEffect(() => {
        const timer = setTimeout(() => setSettledSignature(signature), 1000);
        return () => clearTimeout(timer);
    }, [signature]);

    useEffect(() => {
        if (pagedUnavailable) return undefined;
        let cancelled = false;
        axios.get(`${process.env.REACT_APP_API_URL}/conversations`, {
            params: {
                paged: true,
                tab: TAB_NAMES[activeKey] || 'queued',
                skip: (page - 1) * pageSize,
                limit: pageSize,
                ...(page > 1 && cursors.current[page] ? {after: cursors.current[page]} : {}),
                ...(searchQuery ? {search: searchQuery} : {})
            }
        }).then(({data}) => {
            if (cancelled) return;
            if (!data || Array.isArray(data) || !Array.isArray(data.data)) {
                setPagedUnavailable(true);
                return;
            }
            conversationsPageLoaded(data.data);
            if (data.nextCursor) cursors.current[page + 1] = data.nextCursor;
            setServer({key: `${activeKey}|${page}|${searchQuery}`, ids: data.data.map(c => c._id), rows: data.data, total: data.total || 0, counts: data.counts || {}, hasMore: data.hasMore});
        }).catch(error => {
            if (cancelled) return;
            console.log(error);
            setPagedUnavailable(true);
        });
        return () => {
            cancelled = true;
        };
    }, [activeKey, page, pageSize, searchQuery, settledSignature, pagedUnavailable]);

    // The loaded page belongs to this tab, page and search (while another loads, the loaded conversations show).
    const serverMode = !pagedUnavailable && !!server && server.key === `${activeKey}|${page}|${searchQuery}`;
    const sortedList = useMemo(() => newestFirst(active.list.filter(conversation => matchesSearch(conversation, query))),
        [active.list, query]);

    const serverRows = useMemo(() => {
        if (!serverMode) return [];
        const byId = new Map(messages.map(c => [c._id, c]));
        const rows = server.ids.map((id, index) => byId.get(id) || server.rows[index]).filter(Boolean);
        // New conversations of this tab that arrived since the page was loaded (first page, no search).
        if (page === 1 && !searchQuery) {
            const shown = new Set(server.ids);
            const newest = rows.length ? Math.max(...rows.map(c => new Date(c.lastMessage?.time || 0).getTime())) : 0;
            active.list.forEach(c => {
                if (!shown.has(c._id) && (!c.lastMessage || new Date(c.lastMessage.time).getTime() > newest)) rows.push(c);
            });
        }
        return newestFirst(rows);
    }, [serverMode, server, messages, active.list, page, searchQuery]);

    const totalCount = serverMode ? server.total : sortedList.length;
    const pages = Math.max(1, Math.ceil(totalCount / pageSize));
    useEffect(() => {
        if (page > pages) setPage(pages);
    }, [pages]);
    const visible = useMemo(() => (serverMode ? serverRows : sortedList.slice((page - 1) * pageSize, page * pageSize)),
        [serverMode, serverRows, sortedList, page]);
    const goTo = next => {
        setPage(next);
        const list = listRef.current;
        if (list) list.scrollTop = 0;
    };

    // Fit the page to the list: its height divided by a row's height.
    useEffect(() => {
        const list = listRef.current;
        if (!list) return undefined;
        let timer;
        const measure = () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                const row = list.querySelector('.inbox-row');
                const rowHeight = (row && row.offsetHeight) || ROW_FALLBACK;
                const fits = Math.floor(list.clientHeight / rowHeight);
                const next = Math.min(MAX_PAGE, Math.max(MIN_PAGE, fits));
                setPageSize(current => (current === next ? current : next));
            }, 150);
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (observer) observer.observe(list);
        window.addEventListener('resize', measure);
        return () => {
            clearTimeout(timer);
            if (observer) observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [visible.length > 0, pages > 1]);

    // J / K move through the list, / jumps to the search (not while typing somewhere).
    useEffect(() => {
        const onKey = e => {
            if (e.metaKey || e.ctrlKey || e.altKey || isEditableTarget(e.target)) return;
            if (e.key === '/') {
                e.preventDefault();
                searchRef.current && searchRef.current.focus();
                return;
            }
            if (e.key !== 'j' && e.key !== 'k' && e.key !== 'J' && e.key !== 'K') return;
            if (!visible.length) return;
            e.preventDefault();
            const index = visible.findIndex(conversation => conversation._id === global.chatId);
            const step = e.key.toLowerCase() === 'j' ? 1 : -1;
            const next = visible[index === -1 ? (step > 0 ? 0 : visible.length - 1) : Math.min(visible.length - 1, Math.max(0, index + step))];
            if (next && next._id !== global.chatId) {
                onSelectConversation(next);
                const row = rowRefs.current[next._id];
                if (row) {
                    row.scrollIntoView({block: 'nearest'});
                    row.focus({preventScroll: true});
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [visible, global.chatId]);

    const countFor = tab => {
        const fromServer = serverMode ? server.counts?.[TAB_NAMES[tab.key]] : null;
        const unread = fromServer ? fromServer.unread : tab.list.filter(conversation => !conversation.isRead).length;
        const size = fromServer ? fromServer.total : tab.list.length;
        return unread
            ? <span className="inbox-tabs__count is-unread" title={t('chats.unreadCount', '{n} unread', {n: unread})}>{unread}</span>
            : size ? <span className="inbox-tabs__count">{size}</span> : null;
    };

    return (
        <div className={"gx-chat-sidenav-main inbox " + classList}>
            <div className="gx-chat-sidenav-content chat-left-part inbox__panel">
                <div className="inbox__search">
                    <SearchOutlined aria-hidden="true"/>
                    <input
                        ref={searchRef}
                        type="search"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Escape') {
                                setQuery('');
                                e.currentTarget.blur();
                            }
                        }}
                        placeholder={t('chats.searchPlaceholder', 'Search name, number or message')}
                        aria-label={t('chats.searchPlaceholder', 'Search name, number or message')}
                    />
                    {query ? (
                        <button type="button" className="inbox__search-clear" onClick={() => setQuery('')} aria-label={t('chats.clearSearch', 'Clear search')}>
                            <CloseCircleFilled/>
                        </button>
                    ) : <kbd className="inbox__kbd" aria-hidden="true">/</kbd>}
                </div>
                <div className="inbox-tabs" role="tablist" aria-label={t('chats.tabsLabel', 'Conversations')}>
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={tab.key === activeKey}
                            className={`inbox-tabs__tab${tab.key === activeKey ? ' is-active' : ''}${tab.key === '2' ? ' is-queue' : ''}`}
                            onClick={() => setTabId(tab.key)}
                        >
                            <span className="inbox-tabs__label">{tab.label}</span>
                            {countFor(tab)}
                        </button>
                    ))}
                </div>
                <div className="inbox__list" role="listbox" aria-label={active.label} ref={listRef}>
                    {visible.length ? visible.map(conversation => (
                        <InboxRow
                            key={conversation._id}
                            rowRef={el => {
                                rowRefs.current[conversation._id] = el;
                            }}
                            conversation={conversation}
                            selected={conversation._id === global.chatId}
                            queued={queued}
                            now={now}
                            onSelect={onSelectConversation}
                            onTake={queued ? onTake : null}
                        />
                    )) : (
                        <div className="inbox__none">
                            {query
                                ? t('chats.noMatch', 'No conversations match “{q}”', {q: query.trim()})
                                : <IntlMessages id="chats.noConversationsFound"/>}
                        </div>
                    )}
                </div>
                {pages > 1 ? (
                    <div className="inbox__pager">
                        <button type="button" disabled={page <= 1} onClick={() => goTo(page - 1)} aria-label={t('chats.prevPage', 'Newer conversations')}>
                            <LeftOutlined/>
                        </button>
                        <span>{t('chats.pageRange', '{from}–{to} of {total}', {
                            from: (page - 1) * pageSize + 1,
                            to: Math.min(page * pageSize, totalCount),
                            total: totalCount
                        })}</span>
                        <button type="button" disabled={serverMode && typeof server.hasMore === 'boolean' ? !server.hasMore : page >= pages} onClick={() => goTo(page + 1)} aria-label={t('chats.nextPage', 'Older conversations')}>
                            <RightOutlined/>
                        </button>
                    </div>
                ) : null}
            </div>

            <ConversationsLink link="/my-archives" labelKey="conversationsListArchive"/>
        </div>
    );
}

const mapStateToProps = ({global, messages, accounts}) => ({global, accounts, messages});

export default injectIntl(connect(mapStateToProps, {
    setTabId,
    setChatId,
    markConversationRead,
    createConversation,
    changeConversationSender,
    turnScrollOn,
    assignToMe,
    setAssigned,
    conversationsPageLoaded
})(ConversationsList));
